import * as React from 'react'
import type { Temporal } from '@js-temporal/polyfill'

import WebAPIUtils from '~publish/legacy/composer/composer/utils/WebAPIUtils'
import { format, startOfMonth, endOfMonth } from '~publish/helpers/temporal'
import pMemoize, { pMemoizeClear } from 'p-memoize'

export type Slot = {
  timestamp: number
  isSlotFree: boolean
}

export type SlotData = Record<string, Array<Slot>>

export interface UseChannelSlotsResult {
  loading: boolean
  error: Error | null
  slots: ReadonlyArray<Slot> | null
}

const memoFetchProfiles = pMemoize(fetchProfileSlotData, {
  cacheKey: ([{ channelId, startDateString, endDateString }]) =>
    `${channelId}-${startDateString}-${endDateString}`,
})

const clearCache = (): void => {
  pMemoizeClear(memoFetchProfiles)
}

export function useChannelSlots({
  channelId,
  dateTime,
}: {
  channelId: string | undefined
  dateTime: Temporal.ZonedDateTime | undefined
}): UseChannelSlotsResult {
  const requestIdRef = React.useRef<number>(0)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | null>(null)
  const [slots, setSlots] = React.useState<ReadonlyArray<Slot> | null>(null)

  React.useEffect(() => {
    // clear cache when channelId changes
    clearCache()
  }, [channelId])

  React.useEffect((): (() => void) => {
    return function cleanupOnUnmount(): void {
      // use NaN to mark as unmounted to prevent updates after unmount
      requestIdRef.current = NaN
      clearCache()
    }
  }, [])

  const executeFetchSlots = React.useCallback(async (): Promise<void> => {
    if (!channelId || !dateTime) {
      setSlots(null)
      setLoading(false)
      setError(null)

      return
    }

    const currentRequestId = ++requestIdRef.current
    setLoading(true)
    setError(null)
    setSlots(null)

    try {
      const slotsForMonth = await memoFetchProfiles({
        channelId,
        startDateString: startOfMonth(dateTime).toPlainDate().toString(),
        endDateString: endOfMonth(dateTime).toPlainDate().toString(),
      })

      const dateKey = format(dateTime, 'yyyy-MM-dd')
      const slotsForDate = slotsForMonth[dateKey] ?? null
      if (requestIdRef.current === currentRequestId) {
        // only set slots if this is the last started promise
        setSlots(slotsForDate)
      }
    } catch (err) {
      if (requestIdRef.current === currentRequestId) {
        const errorMessage =
          err instanceof Error ? err : new Error('Failed to fetch slots')
        setError(errorMessage)
        setSlots(null)
      }
    } finally {
      if (requestIdRef.current === currentRequestId) {
        setLoading(false)
      }
    }
  }, [channelId, dateTime])

  React.useEffect(() => {
    void executeFetchSlots()
  }, [executeFetchSlots])

  return {
    loading,
    error,
    slots,
  }
}

/**
 * Helper function to fetch profile slot data from the API
 * @param channelId - The channel ID to fetch slots for
 * @param startDateString - Start date in YYYY-MM-DD format
 * @param endDateString - End date in YYYY-MM-DD format
 * @returns Promise<ApiSlotsResponse> - Nested object with dates as keys and slot arrays as values
 */
async function fetchProfileSlotData({
  channelId,
  startDateString,
  endDateString,
}: {
  channelId: string
  startDateString: string
  endDateString: string
}): Promise<SlotData> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return await WebAPIUtils.getProfileSlotDataForDateRange(
    channelId,
    startDateString,
    endDateString,
  )
}
